import { portalPaths } from '@provision/portal-routes';
import { useMemo } from 'react';
import { useUserGroupRuleValues } from 'src/graphql';
import {
  CustomView,
  CustomViewNavItem,
  useCustomViewMenuItems,
} from 'src/graphql/entities/custom-view';
import { hasWorkflowManagementAccess } from 'src/graphql/entities/workflow';
import {
  SETTINGS_NAVIGATION_GROUPS,
  type SettingsNavigationGroup,
} from 'src/pages/Settings/settings-menu-items';
import { type NavigationDestination } from 'src/utils/navigation/navigation.types';
import { useNavigationAccess } from 'src/utils/navigation/use-navigation-access';
import { useWorkspaceItems } from 'src/utils/navigation/use-workspace-items';

interface NavigationDestinations {
  workspaceItems: NavigationDestination[];
  favoriteViews: CustomViewNavItem[];
  settingsGroups: SettingsNavigationGroup[];
  customViews: CustomView[];
  reorderFavoriteViews: (views: CustomViewNavItem[]) => void;
  filterByAccess: <Item extends NavigationDestination>(items: Item[]) => Item[];
  isReady: boolean;
  loading: boolean;
}

export function useNavigationDestinations(): NavigationDestinations {
  const { filterByAccess, isReady, loading } = useNavigationAccess();
  const { userGroupRules } = useUserGroupRuleValues();
  const workspaceMenuItems = useWorkspaceItems();
  const { viewsMenuItems, customViews, reorderViews } = useCustomViewMenuItems();

  const workspaceItems = useMemo(
    () => (isReady ? filterByAccess(workspaceMenuItems) : []),
    [isReady, filterByAccess, workspaceMenuItems],
  );

  const favoriteViews = useMemo(
    () => (isReady ? filterByAccess(viewsMenuItems) : []),
    [isReady, filterByAccess, viewsMenuItems],
  );

  const settingsGroups = useMemo(() => {
    if (!isReady) {
      return [];
    }

    return SETTINGS_NAVIGATION_GROUPS.map((group) => ({
      id: group.id,
      title: group.title,
      items: filterByAccess(
        group.items.filter(
          (item) =>
            item.path !== portalPaths.settings.workflows() ||
            hasWorkflowManagementAccess(userGroupRules),
        ),
      ),
    })).filter((group) => group.items.length);
  }, [isReady, filterByAccess, userGroupRules]);

  return {
    workspaceItems,
    favoriteViews,
    settingsGroups,
    customViews,
    reorderFavoriteViews: reorderViews,
    filterByAccess,
    isReady,
    loading,
  };
}
